import React, {useEffect, useState} from "react";

import { useTheme } from '../../provider/themeProvider';

import PopupDownNewSequenceStyle from './PopupDownNewSequence.module.css';
import {useAppContext} from "../../provider/appProvider";

import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import dayjs from "dayjs";
import CameraElement from "../CameraElement";
import api from "../../services/api";
import {usePopup} from "../../services/PopupContext";
import PopupDownNewSequenceLoading from "./PopupDownNewSequenceLoading";
import {useDesktop} from "../../provider/DesktopContext";

// La restitution temps réel remonte les segments bruts du NVR : la plage est bornée
// à 1 heure, comme dans l'application mobile (limite également appliquée côté API).
const REALTIME_MAX_HOURS = 1;

const errorMessage = (code) => {
  switch (code) {
    case 'CODNUM_ERROR_RECORD_PERIOD':
      return `La restitution en temps réel est limitée à ${REALTIME_MAX_HOURS} heure.`;
    case 'CODNUM_ERROR_TIMELAPSE_PERIOD':
      return "Cette période est antérieure à l'enregistrement continu de la caméra.";
    default:
      return 'Une erreur est survenue, veuillez réessayer.';
  }
};

const PopupDownNewSequence = (props) => {
  const { cameraSelect, index } = props;

  const [cameraSelected, setCameraSelected] = useState(cameraSelect);
  const [dateStart, setDateStart] = useState(dayjs(new Date(new Date().setHours(0,0,0,0))));
  const [dateEnd, setDateEnd] = useState(dayjs(new Date()));
  const [detailedVideo, setDetailedVideo] = useState(false);
  const [error, setError] = useState(null);

  const { darkMode } = useTheme();
  const { cameras } = useAppContext();
  const isDesktop = useDesktop();

  const { showPopup, hidePopup } = usePopup();

  const camera = cameras.find((element) => element.id === cameraSelected);

  // Sur une caméra RTSP, le mode détaillé est une restitution temps réel servie par le NVR
  // (route record), et non un timelapse rééchantillonné. Sur une caméra MJPEG il reste un
  // timelapse à 1 image par seconde.
  const realtime = detailedVideo && camera?.screenshot_type === 'RTSP';
  const maxRealtimeEnd = dateStart.add(REALTIME_MAX_HOURS, 'hour');

  const css = {
    '.MuiPickersInputBase-root': {
      backgroundColor: darkMode ? '#303030' : '#F3F3F3',
      border: 0,
      color: darkMode ? '#FFFFFF' : '#0A0908',
      padding: '0 6px',
    },
    'fieldset': {
      border: '0 !important',
    },
    '.MuiSvgIcon-root': {
      color: darkMode ? '#FFFFFF' : '#0A0908',
    },
    '.MuiPickersSectionList-root': {
      padding: '6px 0px',
    }
  }

  const handleCreateSequence = async (event) => {
    setError(null);

    try {
      if (realtime) {
        const response = await api.post(`/cameras/${cameraSelected}/record`, {
          targetTime: (dateStart.unix() * 1000),
          secondsBefore: 0,
          secondsAfter: dateEnd.diff(dateStart, 'second'),
        });

        hidePopup(index);
        showPopup(PopupDownNewSequenceLoading, {recordID: response.data.recordId});
        return;
      }

      const payload = {
        cameraID: cameraSelected,
        startAT: (dateStart.unix() * 1000),
        endAT: (dateEnd.unix() * 1000),
      };

      if (detailedVideo) {
        const diffMinutes = dateEnd.diff(dateStart, 'minute');
        payload.detailed = true;
        payload.outputFps = Math.max(1, Math.floor(diffMinutes / 2));
      }

      const response = await api.post(`/timelapses`, payload);

      hidePopup(index);
      showPopup(PopupDownNewSequenceLoading, {timelapseID: response.data.timelapse.id});
    } catch (err) {
      console.log((err.response && err.response.data) || err.message || err.toString());
      setError(errorMessage(err.response && err.response.data && err.response.data.error));
    }
  }

  // Le passage en temps réel (ou un changement de date de début) peut faire sortir la
  // date de fin de la fenêtre autorisée : on la ramène à la borne.
  useEffect(() => {
    if (!realtime) {
      return;
    }

    const maxEnd = dateStart.add(REALTIME_MAX_HOURS, 'hour');

    if (dateEnd.isAfter(maxEnd)) {
      setDateEnd(maxEnd);
    }
  }, [realtime, dateStart, dateEnd]);

  return (
    <div className={PopupDownNewSequenceStyle.container}>
      <span className={PopupDownNewSequenceStyle.title}>Nouvelle séquence</span>
      <div className={PopupDownNewSequenceStyle.cameras}>
        <span className={PopupDownNewSequenceStyle.text}>À partir de la caméra {cameraSelected ? `: ${cameras.find((camera) => camera.id === cameraSelected).name}` : ''}</span>
        <div className={PopupDownNewSequenceStyle.camerasSelector}>
          {cameras.map((camera, index) => {
            return (
              <div key={index} onClick={() => setCameraSelected(camera.id)}>
                <CameraElement name={camera.name} link={camera.stream_link ?? camera.signedLink} gridSize={99} id={camera.id} type={1} selected={cameraSelected === camera.id}></CameraElement>
              </div>
            );
          })}
        </div>
      </div>
      <div className={PopupDownNewSequenceStyle.dates}>
        <span className={PopupDownNewSequenceStyle.datesText}>Du</span>
        <div className={PopupDownNewSequenceStyle.datesSelector}>
          { isDesktop &&
            <DesktopDateTimePicker
              onChange={(newValue, context) => {
                if (context.validationError == null) {
                  setDateStart(newValue);
                }
              }}
              className={PopupDownNewSequenceStyle.dateTimePicker}
              ampm={false}
              format="DD/MM/YYYY à HH:mm"
              minDateTime={dayjs(new Date(new Date().setMonth(new Date().getMonth()-1)))}
              maxDateTime={dateEnd.add(-1, 'minute')}
              timezone="Europe/Paris"
              defaultValue={dayjs(new Date()).add(-1, 'minute')}
              timeSteps={{ hours: 1, minutes: 1}}
              skipDisabled
              disableFuture
              sx={css}
              enableAccessibleFieldDOMStructure={true}
            />
          }

          { !isDesktop &&
            <MobileDateTimePicker
              onChange={(newValue, context) => {
                if (context.validationError == null) {
                  setDateStart(newValue);
                }
              }}
              className={PopupDownNewSequenceStyle.dateTimePicker}
              ampm={false}
              format="DD/MM/YYYY à HH:mm"
              minDateTime={dayjs(new Date(new Date().setMonth(new Date().getMonth()-1)))}
              maxDateTime={dateEnd.add(-1, 'minute')}
              timezone="Europe/Paris"
              defaultValue={dayjs(new Date()).add(-1, 'minute')}
              timeSteps={{ hours: 1, minutes: 1}}
              skipDisabled
              disableFuture
              sx={css}
              enableAccessibleFieldDOMStructure={true}
            />
          }
        </div>
        <span className={PopupDownNewSequenceStyle.datesText}>au</span>
        <div className={PopupDownNewSequenceStyle.datesSelector}>
          { isDesktop &&
            <DesktopDateTimePicker
              onChange={(newValue, context) => {
                if (context.validationError == null) {
                  setDateEnd(newValue);
                }
              }}
              className={PopupDownNewSequenceStyle.dateTimePicker}
              ampm={false}
              format="DD/MM/YYYY à HH:mm"
              minDateTime={dateStart.add(1, 'minute')}
              maxDateTime={realtime ? maxRealtimeEnd : undefined}
              timezone="Europe/Paris"
              value={dateEnd}
              timeSteps={{ hours: 1, minutes: 1}}
              skipDisabled
              disableFuture
              sx={css}
              enableAccessibleFieldDOMStructure={true}
            />
          }

          { !isDesktop &&
            <MobileDateTimePicker
              onChange={(newValue, context) => {
                if (context.validationError == null) {
                  setDateEnd(newValue);
                }
              }}
              className={PopupDownNewSequenceStyle.dateTimePicker}
              ampm={false}
              format="DD/MM/YYYY à HH:mm"
              minDateTime={dateStart.add(1, 'minute')}
              maxDateTime={realtime ? maxRealtimeEnd : undefined}
              timezone="Europe/Paris"
              value={dateEnd}
              timeSteps={{ hours: 1, minutes: 1}}
              skipDisabled
              disableFuture
              sx={css}
              enableAccessibleFieldDOMStructure={true}
            />
          }
        </div>
      </div>
      <div className={PopupDownNewSequenceStyle.detailedContainer}>
        <label className={PopupDownNewSequenceStyle.detailedLabel}>
          <input
            type="checkbox"
            checked={detailedVideo}
            onChange={(e) => setDetailedVideo(e.target.checked)}
          />
          Vidéo détaillé
        </label>
      </div>
      { realtime &&
        <div className={PopupDownNewSequenceStyle.warning}>
          Restitution en temps réel : la plage est limitée à {REALTIME_MAX_HOURS} heure.
        </div>
      }
      { error &&
        <div className={PopupDownNewSequenceStyle.error}>{error}</div>
      }
      <div className={PopupDownNewSequenceStyle.generateContainer}>
        <input value={'Générer'} type={"button"} className={'btn-01'} disabled={!(cameraSelected && dateStart && dateEnd)} onClick={handleCreateSequence} />
      </div>
    </div>
  );
};

export default PopupDownNewSequence;
